import type { ProductPriceTierPatchRequestType } from "@sellar-io/api-v2/types";
import {
  Button,
  Icon,
  InputContainer,
  InputCurrency,
} from "@sellar-io/components";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { Alert } from "../../../../components/alert/Alert";

import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../components/table/TableLegacy";
import { SlideTransition } from "../../../../components/transition/SlideTransition";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { usePriceTierBaseList } from "../../../../hooks/api/v2/priceTier/usePriceTierBaseList";
import { usePriceTierList } from "../../../../hooks/api/v2/priceTier/usePriceTierList";
import { useProductPriceTierPatchV2 } from "../../../../hooks/api/v2/productPriceTier/useProductPriceTierPatchV2";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../hooks/usePriceFormat";
import { formatCurrency, getDerivedPrice } from "@sellar-io/common";

type PriceListId = number;
type PriceListPricingMap = Record<
  PriceListId,
  {
    priceListId: number;
    baseListId: number | null;
    basePrice: number | null;
    price: number | null;
    existingProductPriceTierId: number | null;
  }
>;

export type ProductItemPricingSectionHandle = {
  save: () => Promise<void>;
};

export const ProductItemPricingSection = forwardRef<
  ProductItemPricingSectionHandle,
  {
    productId: number;
    containerType?: string | null;
    alwaysShowDerivedPrices?: boolean;
  }
>(({ productId, containerType, alwaysShowDerivedPrices = false }, ref) => {
  const [pricingMap, setPricingMap] = useState<PriceListPricingMap>({});
  const [showDerivedPrices, setShowDerivedPrices] = useState(
    alwaysShowDerivedPrices,
  );
  const hasInitializedRef = useRef(false);
  const shouldUpdateRef = useRef(false);

  const productPriceTierPatchMutation = useProductPriceTierPatchV2();
  const { data: basePriceListsData, isFetched } = usePriceTierBaseList({
    limit: 50,
  });
  const { data: existingPriceTiersData, isFetched: isExistingFetched } =
    usePriceTierList({
      limit: 100,
      query: [
        {
          type: "ProductId",
          modifier: "EQUALS",
          value: productId,
        },
      ],
    });

  const basePriceLists = useMemo(
    () => basePriceListsData?.pages.flat() ?? [],
    [basePriceListsData],
  );

  const noDerivedPriceLists = !basePriceLists.some((basePriceList) =>
    basePriceList.DerivedPriceTiers.some(
      (derivedPriceList) => derivedPriceList,
    ),
  );

  const hasDerivedPriceLists = !noDerivedPriceLists;

  const existingPriceTiers = useMemo(
    () => existingPriceTiersData?.pages.flat() ?? [],
    [existingPriceTiersData],
  );

  useEffect(() => {
    if (!isFetched || !isExistingFetched || hasInitializedRef.current) return;
    hasInitializedRef.current = true; // ensures we only initialize once

    const existingById = new Map(
      existingPriceTiers.map((tier) => [
        tier.id,
        tier.ProductPriceTiers[0] ?? null,
      ]),
    );

    const newMap: PriceListPricingMap = {};

    basePriceLists.forEach((base) => {
      const existingPPT = existingById.get(base.id);
      newMap[base.id] = {
        priceListId: base.id,
        baseListId: null,
        basePrice: null,
        price: existingPPT?.price ?? null,
        existingProductPriceTierId: existingPPT?.id ?? null,
      };
      base.DerivedPriceTiers.forEach((derivedList) => {
        const existingDerivedPPT = existingById.get(derivedList.id);
        newMap[derivedList.id] = {
          priceListId: derivedList.id,
          baseListId: base.id,
          basePrice: existingPPT?.price ?? null,
          price: existingDerivedPPT?.price ?? null,
          existingProductPriceTierId: existingDerivedPPT?.id ?? null,
        };
      });
    });
    setPricingMap(newMap);
  }, [basePriceLists, existingPriceTiers, isFetched, isExistingFetched]);

  const handlePriceUpdate = (price: number | null, PriceListId: number) => {
    shouldUpdateRef.current = true;
    const newMap = { ...pricingMap };

    Object.entries(pricingMap)
      .filter(
        ([, { priceListId, baseListId }]) =>
          priceListId === PriceListId || baseListId === PriceListId,
      )
      .forEach(([, { priceListId, baseListId }]) => {
        const priceList = basePriceLists
          .find((list) => list.id === baseListId)
          ?.DerivedPriceTiers.find((list) => list.id === priceListId);
        if (priceListId) {
          newMap[priceListId] = {
            ...newMap[priceListId],
            priceListId: priceListId,
            baseListId: baseListId ?? null,
            basePrice: !!baseListId ? price : null,
            existingProductPriceTierId:
              newMap[priceListId]?.existingProductPriceTierId ?? null,
            price:
              !!baseListId && priceList
                ? getDerivedPrice({
                    basePrice: price ?? 0,
                    containerType: containerType ?? "keg",
                    productPriceTier: {
                      price,
                      derivationType: "inherit",
                      derivationValue: null,
                    },
                    priceTier: priceList,
                    priceTierContainerDiscounts:
                      priceList.PriceTierContainerDiscounts,
                  })
                : price,
          };
        }
      });

    setPricingMap(newMap);
  };

  const save = async () => {
    if (!shouldUpdateRef.current) return;

    const operations: ProductPriceTierPatchRequestType = Object.values(
      pricingMap,
    )
      .filter(
        ({ price, baseListId }) =>
          price !== null && price !== undefined && baseListId === null,
      )
      .map((pricing) => {
        if (pricing.existingProductPriceTierId !== null) {
          return {
            action: "PUT" as const,
            id: pricing.existingProductPriceTierId,
            value: {
              price: pricing.price ?? 0,
              discountable: true,
            },
          };
        }
        return {
          action: "POST" as const,
          value: {
            PriceTierId: pricing.priceListId,
            ProductId: productId,
            basePrice: pricing.basePrice,
            price: pricing.price ?? 0,
            discountable: true,
            active: true,
          },
        };
      });

    if (operations.length === 0) return;

    await productPriceTierPatchMutation.mutateAsync(operations);
  };

  useImperativeHandle(ref, () => ({ save }));

  return (
    <div className="flex flex-col gap-4">
      {productPriceTierPatchMutation.isError && (
        <Alert
          type="error"
          message={
            apiV2AssertError(productPriceTierPatchMutation.error)?.response
              ?.data.message ?? "Unknown error"
          }
        />
      )}

      <TableLegacyContainer className="max-h-75">
        <TableLegacy>
          <TableLegacyTHead>
            <TableLegacyTR className="text-xs font-semibold">
              <TableLegacyTH className="sticky top-0 w-1/2">
                Base price list
              </TableLegacyTH>
              <TableLegacyTH className="sticky top-0">
                Unit price (ex. VAT)
              </TableLegacyTH>
            </TableLegacyTR>
          </TableLegacyTHead>
          <TableLegacyTBody>
            {basePriceLists.map((basePriceList) => (
              <TableLegacyTR key={basePriceList.id}>
                <TableLegacyTD className="text-zinc-500">
                  {basePriceList.name}
                </TableLegacyTD>
                <TableLegacyTD>
                  <InputContainer className="w-full max-w-50">
                    <InputCurrency
                      showPrefix
                      allowNegative
                      placeholder="0.00"
                      value={
                        pricingMap[basePriceList.id]?.price !== null &&
                        pricingMap[basePriceList.id]?.price !== undefined
                          ? formatPriceApiToInput(
                              pricingMap[basePriceList.id]?.price ?? 0,
                            )
                          : undefined
                      }
                      onChange={(e) => {
                        const price = formatPriceInputToApi(e.target.value);
                        handlePriceUpdate(price, basePriceList.id);
                      }}
                    />
                  </InputContainer>
                </TableLegacyTD>
              </TableLegacyTR>
            ))}
          </TableLegacyTBody>
        </TableLegacy>
      </TableLegacyContainer>

      {hasDerivedPriceLists && (
        <div className="flex flex-col gap-2">
          {!alwaysShowDerivedPrices && (
            <Button
              color="transparent"
              onClick={() => setShowDerivedPrices((prev) => !prev)}
              className={twMerge(showDerivedPrices && "bg-zinc-100")}
            >
              Show derived prices
              <Icon icon="CHEVRON_RIGHT" />
            </Button>
          )}

          <SlideTransition show={showDerivedPrices}>
            <TableLegacyContainer className="max-h-75">
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR className="text-xs font-semibold">
                    <TableLegacyTH className="sticky top-0 w-1/2">
                      Price list
                    </TableLegacyTH>
                    <TableLegacyTH className="sticky top-0">
                      Base price
                    </TableLegacyTH>
                    <TableLegacyTH className="sticky top-0">
                      Derived price
                    </TableLegacyTH>
                  </TableLegacyTR>
                </TableLegacyTHead>
                <TableLegacyTBody>
                  {basePriceLists.map((basePriceList) =>
                    basePriceList.DerivedPriceTiers.map((priceList) => {
                      const basePrice = pricingMap[priceList.id]?.basePrice;
                      const price = pricingMap[priceList.id]?.price;
                      const hasBasePrice =
                        basePrice !== null && basePrice !== undefined;
                      const hasPrice = price !== null && price !== undefined;
                      return (
                        <TableLegacyTR key={priceList.id}>
                          <TableLegacyTD className="font-semibold">
                            {priceList.name}
                          </TableLegacyTD>
                          <TableLegacyTD
                            className={twMerge(
                              "text-zinc-500",
                              hasBasePrice && "line-through",
                            )}
                          >
                            {hasBasePrice ? formatCurrency(basePrice) : "£"}
                          </TableLegacyTD>
                          <TableLegacyTD>
                            {hasPrice
                              ? formatCurrency(
                                  pricingMap[priceList.id]?.price ?? 0,
                                )
                              : "£"}
                          </TableLegacyTD>
                        </TableLegacyTR>
                      );
                    }),
                  )}
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>
          </SlideTransition>
        </div>
      )}

      {noDerivedPriceLists && (
        <Alert
          type="information"
          title="No derived price lists defined"
          message="You can only add pricing to your derived price lists as your old migrated price tiers are uneditable. Replace your old price tiers with price lists to have this product's pricing appear in them"
        />
      )}
    </div>
  );
});

ProductItemPricingSection.displayName = "ProductItemPricingSection";
